import axios from "axios";

import * as api from "../../types";
import { IProductVariant } from "../../../types";

export interface IFetchAllProductsProps {
    status: string[];
    token: string;
}

export interface ICreateProductProps {
    title: string;
    description: string;
    tagLine: string;
    featureScore: number;
    bestSellerScore: number;
    slug: string;
    shopifyProductId: string;
    variants: IProductVariant[];
    status: number;
    token: string;
    meta: Record<string, unknown>;
    schemaId?: string;
    value?: object;
}

export interface IUpdateProductParameters {
    id: string;
    title: string;
    description: string;
    tagLine: string;
    featureScore: number;
    bestSellerScore: number;
    slug: string;
    shopifyProductId: string;
    status: number;
    token: string;
    schemaId?: string;
    value?: object;
}

export interface ICreateProductVariantParameters {
    productId: string;
    title: string;
    shopifyVariantId: string;
    price: number;
    mrp: number;
    shippingPrice: number;
    sku: string;
    position: number;
    status: number;
    meta?: any;
    amazonRedirectUrl?: string | null;
    flipkartRedirectUrl?: string | null;
    token: string;
}

export interface IUpdateProductVariantParameters {
    id: string;
    productId: string;
    title: string;
    shopifyVariantId: string;
    price: number;
    mrp: number;
    shippingPrice: number;
    sku: string;
    position: number;
    status: number;
    meta?: any;
    amazonRedirectUrl?: string | null;
    flipkartRedirectUrl?: string | null;
    token: string;
}

export interface IPackagingRule {
    id: string;
    variantId: string;
    maxUnits: number;
    material: number;
    units: number;
    createdAt: Date;
}

export interface ICreatePackagingRuleParameters {
    variantId: string;
    productId: string;
    maxUnits: number;
    material: number;
    units: number;
    token: string;
}

export interface IUpdatePackagingRuleParameters {
    id: string;
    variantId: string;
    productId: string;
    maxUnits?: number;
    material?: number;
    units?: number;
    token: string;
}

export interface IReelLink {
    id: string;
    url: string;
    thumbnailUrl?: string | null;
    position: number;
    variantId: string;
    productId: string;
}

export interface ICreateReelParameters {
    variantId: string;
    productId: string;
    url: string;
    thumbnailUrl?: string;
    position: number;
    token: string;
}

export interface IFetchProductParameters {
    slug: string | undefined;
    token: string;
}

export class Products {
    private static transform(data: any): api.IProduct {
        return {
            ...data,
            createdAt: new Date(data.createdAt),
            updatedAt: new Date(data.updatedAt),
        };
    }

    public static async fetchAllProducts(
        parameters: IFetchAllProductsProps,
    ): Promise<api.IProduct[]> {
        const { token, ...body } = parameters;

        const params = new URLSearchParams([
            ...body.status.map((status) => ["status", status]),
        ]);

        const { data } = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
                params,
            },
        );

        return data.map(this.transform);
    }

    public static async fetchProduct(
        parameters: IFetchProductParameters,
    ): Promise<api.IProduct> {
        const { slug, token } = parameters;
        const { data } = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products/${slug}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
            },
        );

        return this.transform(data);
    }

    public static async createProduct(
        parameters: ICreateProductProps,
    ): Promise<api.IProduct> {
        const { token, ...body } = parameters;

        const { data } = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
            },
        );

        return this.transform(data);
    }

    public static async updateProduct(
        parameters: IUpdateProductParameters,
    ): Promise<api.IProduct> {
        const { token, id, ...body } = parameters;

        const { data } = await axios.patch(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products/${id}`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
            },
        );
        return this.transform(data);
    }

    public static async createVariant(
        parameters: ICreateProductVariantParameters,
    ): Promise<api.IProduct> {
        const { token, productId, ...body } = parameters;

        const { data } = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products/${productId}/variants`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
            },
        );
        return this.transform(data);
    }

    public static async updateVariant(
        parameters: IUpdateProductVariantParameters,
    ): Promise<api.IProduct> {
        const { token, id, productId, ...body } = parameters;

        const { data } = await axios.patch(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products/${productId}/variants/${id}`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
            },
        );
        return this.transform(data);
    }

    public static async getPackagingRules(
        variantId: string,
        productId: string,
        token: string
    ): Promise<IPackagingRule[]> {
        if (!variantId || !productId) {
            throw new Error("variantId and productId are required");
        }

        const { data } = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products/${productId}/variants/${variantId}/packaging-rules`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
            }
        );
        return data;
    }

    public static async createPackagingRule(
        parameters: ICreatePackagingRuleParameters
    ): Promise<IPackagingRule> {
        const { token, variantId, productId, ...body } = parameters;

        if (!variantId || !productId) {
            throw new Error("variantId and productId are required");
        }

        const { data } = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products/${productId}/variants/${variantId}/packaging-rules`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
            }
        );
        return data;
    }

    public static async createReel(
        parameters: ICreateReelParameters
    ): Promise<IReelLink> {
        const { token, variantId, productId, ...body } = parameters;

        const { data } = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products/${productId}/variants/${variantId}/reels`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
            }
        );
        return data;
    }

    public static async deleteReel(
        reelId: string,
        variantId: string,
        productId: string,
        token: string
    ): Promise<void> {
        await axios.delete(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products/${productId}/variants/${variantId}/reels/${reelId}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
            }
        );
    }

    public static async getBlocks(
        productId: string,
        variantId: string,
        token: string
    ): Promise<api.IContentBlockNode[]> {
        const { data } = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products/${productId}/variants/${variantId}/blocks`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
            }
        );
        return data;
    }

    public static async createBlock(
        productId: string,
        variantId: string,
        body: {
            type: api.ContentBlockType;
            properties: Record<string, unknown>;
            parentId?: string;
            afterId?: string | null;
        },
        token: string
    ): Promise<api.IContentBlock> {
        const { data } = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products/${productId}/variants/${variantId}/blocks`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
            }
        );
        return data;
    }

    public static async updateBlock(
        productId: string,
        variantId: string,
        blockId: string,
        body: {
            properties?: Record<string, unknown>;
            parentId?: string | null;
            afterId?: string | null;
        },
        token: string
    ): Promise<api.IContentBlock> {
        const { data } = await axios.patch(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products/${productId}/variants/${variantId}/blocks/${blockId}`,
            body,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
            }
        );
        return data;
    }

    public static async deleteBlock(
        productId: string,
        variantId: string,
        blockId: string,
        token: string
    ): Promise<void> {
        await axios.delete(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/products/${productId}/variants/${variantId}/blocks/${blockId}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Admin-Request": "true",
                },
            }
        );
    }
}
